// packages/web/src/components/QuickNavigator.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface NavSection {
  label: string;
  icon: string;
  color: string;
  items: { label: string; path: string; icon: string }[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Recruitment',
    icon: '🎯',
    color: '#0ea5e9',
    items: [
      { label: 'Manpower Requisition', path: '/manpower-requisition', icon: '📋' },
      { label: 'Interview Assessment', path: '/interview-assessment', icon: '🎤' },
      { label: 'Employee Lifecycle', path: '/employee-lifecycle', icon: '🔄' },
    ],
  },
  {
    label: 'Core HR',
    icon: '👥',
    color: '#10b981',
    items: [
      { label: 'Employees', path: '/employees', icon: '👤' },
      { label: 'Onboarding', path: '/onboarding-offboarding', icon: '🚀' },
      { label: 'Masters Data', path: '/masters', icon: '⚙️' },
    ],
  },
  {
    label: 'Time & Attendance',
    icon: '⏰',
    color: '#06b6d4',
    items: [
      { label: 'Attendance', path: '/attendance', icon: '📊' },
      { label: 'Attendance Muster', path: '/attendance-muster', icon: '📋' },
      { label: 'Leave Management', path: '/leave-management', icon: '🌴' },
      { label: 'Overtime Management', path: '/overtime-management', icon: '⏱️' },
      { label: 'Comp-Off', path: '/comp-off', icon: '🔄' },
      { label: 'Holiday List', path: '/holiday-list', icon: '🎉' },
    ],
  },
  {
    label: 'Payroll & Finance',
    icon: '💰',
    color: '#f59e0b',
    items: [
      { label: 'Payroll', path: '/payroll', icon: '💵' },
      { label: 'Payroll Management', path: '/payroll-management', icon: '📊' },
      { label: 'Salary Revision', path: '/salary-revision', icon: '📈' },
      { label: 'My Payslips', path: '/my-payslips', icon: '🧾' },
      { label: 'IT Declaration', path: '/it-declaration', icon: '📝' },
      { label: 'Reimbursements', path: '/reimbursements', icon: '💳' },
    ],
  },
  {
    label: 'Management',
    icon: '📊',
    color: '#8b5cf6',
    items: [
      { label: 'Performance', path: '/performance', icon: '🏆' },
      { label: 'Workforce Management', path: '/workforce-management', icon: '📋' },
      { label: 'Task Management', path: '/task-management', icon: '✅' },
      { label: 'Training', path: '/training', icon: '📚' },
    ],
  },
  {
    label: 'Exit & Alumni',
    icon: '🚪',
    color: '#f43f5e',
    items: [
      { label: 'Exit Management', path: '/exit-management', icon: '📤' },
      { label: 'Settlement (F&F)', path: '/settlement', icon: '💼' },
      { label: 'Alumni Network', path: '/alumni', icon: '🎓' },
    ],
  },
];

export default function QuickNavigator() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.4rem 0.75rem', background: open ? '#eef2ff' : '#f1f5f9',
          border: `1px solid ${open ? '#c7d2fe' : '#e2e8f0'}`, borderRadius: 8,
          color: open ? '#4f46e5' : '#64748b', fontSize: '0.8rem', cursor: 'pointer',
          fontFamily: 'inherit', fontWeight: 500, transition: 'all 0.15s',
        }}
      >
        🧭 Navigate
        <span style={{ fontSize: '0.6rem', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.12)', zIndex: 999,
          width: 520, maxHeight: '75vh', overflowY: 'auto',
          padding: '0.75rem',
        }}>
          <div style={{ padding: '0.25rem 0.5rem 0.5rem', fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Quick Navigation
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            {NAV_SECTIONS.map(section => (
              <div key={section.label} style={{
                border: '1px solid #f1f5f9', borderRadius: 8, padding: '0.5rem',
                background: '#fafbfc',
              }}>
                <div style={{
                  fontSize: '0.75rem', fontWeight: 700, color: section.color,
                  marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.3rem',
                }}>
                  <span>{section.icon}</span> {section.label}
                </div>

                {section.items.map(item => (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                      width: '100%', padding: '0.3rem 0.4rem', border: 'none',
                      background: 'transparent', borderRadius: 6,
                      color: '#374151', fontSize: '0.78rem', cursor: 'pointer',
                      textAlign: 'left', transition: 'background 0.1s',
                      fontFamily: 'inherit',
                    }}
                    onMouseOver={e => (e.currentTarget.style.background = '#eef2ff')}
                    onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{ fontSize: '0.7rem' }}>{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
